/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  AutocompleteClasses,
  AutocompleteRenderOptionState,
  Box,
  InputProps,
  ListSubheader,
  Popper,
  PopperProps,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import VirtualList from 'components/DataGrid/grid/List';
import zIndex from 'constants/ZIndex';
import useComponentSize from 'hooks/useComponentSize';
import React, {
  HTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { stopPropagation } from 'utils/events';
import { isEmpty } from 'utils/isEmpty';
import useJoinRef from 'utils/useJoinRef';

export type SizeType = 'small' | 'medium';
export type Option = Record<string | number, unknown>;

interface BaseSelectInputProps<T extends Option> {
  'data-testid'?: string;
  IOInputProps?: Partial<InputProps>;
  autoCompleteClasses?: Partial<AutocompleteClasses>;
  autoFocus?: boolean;
  className?: string;
  disableClearable?: boolean;
  disableUnderline?: boolean;
  disabled?: boolean;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  groupBy?: (option: T) => string;
  helpText?: string;
  inputValue?: string;
  label?: string;
  labelAttribute?: string;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onOpen?: (e: React.SyntheticEvent<Element>) => void;
  onInputChange?: (value: string, reason: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  open?: boolean;
  openOnFocus?: boolean;
  options: T[];
  loading?: boolean;
  placeholder?: string;
  required?: boolean;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState,
  ) => ReactNode;
  size?: SizeType;
  startIcon?: ReactNode;
  valueAttribute?: string;
  variant: 'outlined' | 'standard' | 'filled';
  labelColor?: string;
  labelFontWeight?: number;
  labelFontStyle?: boolean;
  id?: string;
  sx?: SxProps;
  multi?: boolean;
  limitTags?: number;
  formHorizontal?: boolean;
  error?: boolean;
  unknownValueMessage?: string;
  hideSelectedOptions?: boolean;
}

export interface SelectInputProps<T extends Option>
  extends BaseSelectInputProps<T> {
  onChange: (
    value: string,
    option: T | null,
    options?: T[] | null | undefined,
  ) => void;
  value?: string | number | boolean;
  delimiter?: string;
  selectOptionOnTab?: boolean;
  AsyncSelect?: boolean;
  selectOptionOnBlur?: boolean;
  disableSelection?: boolean;
  isGridHeaderFilter?: boolean;
  optionsWidth?: number | null;
  ignoreSpecialCharacters?: boolean;
  specialCharacters?: string;
}

export interface MultiSelectInputProps<T extends Option>
  extends BaseSelectInputProps<T> {
  onChange: (value: string[], option: T[]) => void;
  value: string[];
  limitTags?: number;
}

const IOPopper = (props: PopperProps) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      // to prevent the canvas grid to receive the mouse move event
      onMouseMove={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onClick={stopPropagation}
    />
  );
};

const SelectInput = <T extends Option>(
  {
    autoCompleteClasses = {},
    open,
    openOnFocus,
    autoFocus,
    onBlur,
    onFocus,
    onOpen,
    onMouseDown,
    onKeyDown,
    id,
    label,
    value,
    onChange,
    onInputChange,
    options,
    loading,
    disableClearable = false,
    placeholder,
    helpText,
    groupBy,
    className,
    disableUnderline = false,
    startIcon,
    endIcon,
    renderOption,
    disabled = false,
    size,
    variant,
    valueAttribute = 'value',
    labelAttribute = 'label',
    required,
    fullWidth,
    IOInputProps,
    'data-testid': dataTestId,
    sx,
    optionsWidth,
  }: SelectInputProps<T>,
  ref: React.Ref<HTMLInputElement>,
): React.ReactElement => {
  const selectedOption = options.find(
    (option) => option[valueAttribute] === value,
  );
  const _onChange = (option: T | null) => {
    onChange(option ? (option[valueAttribute] as string) : '', option);
  };
  const classes = useStyles({ size });

  const renderOptionWithOverflow = (propsLi: any, option: T) => {
    const labelVal = option[labelAttribute] ?? option[valueAttribute];

    return (
      <li
        {...propsLi}
        style={{
          ...propsLi.style,
          whiteSpace: 'nowrap',
          overflow: 'visible',
          textOverflow: 'initial',
        }}
      >
        {typeof labelVal === 'string' ? labelVal : String(labelVal)}
      </li>
    );
  };

  const componentsProps =
    optionsWidth != null
      ? {
          popper: {
            style: {
              width: optionsWidth,
              maxWidth: 'none' as const,
            },
          },
          paper: {
            style: {
              minWidth: optionsWidth,
              width: optionsWidth,
              maxWidth: 'none',
              overflowX: 'auto' as const,
            },
          },
        }
      : undefined;
  return (
    <Autocomplete
      id={id}
      fullWidth={fullWidth}
      open={open}
      openOnFocus={openOnFocus}
      onBlur={onBlur}
      onFocus={onFocus}
      onOpen={onOpen}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      multiple={false}
      PopperComponent={IOPopper}
      disabled={disabled}
      value={selectedOption ?? null}
      disableClearable={disableClearable}
      options={options}
      loading={loading}
      getOptionLabel={(option: T) => {
        const labelVal = option[labelAttribute] ?? option[valueAttribute];
        return typeof labelVal === 'string' ? labelVal : String(labelVal);
      }}
      groupBy={groupBy}
      onChange={(event, option: T | null) => {
        _onChange(option);
      }}
      onInputChange={(event, val: string, reason) => {
        if (onInputChange) {
          onInputChange(val, reason);
        }
      }}
      filterOptions={onInputChange ? (x) => x : undefined}
      classes={{
        root: autoCompleteClasses.root,
        inputRoot: clsx(
          disableUnderline && !autoCompleteClasses.inputRoot
            ? classes.inputRoot
            : undefined,
          autoCompleteClasses.inputRoot,
        ),
        input: clsx(classes.input, autoCompleteClasses.input),
        listbox: clsx(classes.listbox, autoCompleteClasses.listbox),
        popper: clsx(classes.popper, autoCompleteClasses.popper),
        endAdornment: autoCompleteClasses.endAdornment,
      }}
      className={className}
      renderInput={(params) => (
        <CustomTextField
          data-testid={dataTestId}
          ref={ref}
          autoFocus={autoFocus}
          params={params}
          label={label}
          placeholder={placeholder}
          helpText={helpText}
          disableUnderline={disableUnderline}
          startIcon={startIcon}
          endIcon={endIcon}
          variant={variant}
          required={required}
          IOInputProps={IOInputProps}
        />
      )}
      renderOption={
        optionsWidth != null
          ? renderOption ?? renderOptionWithOverflow
          : renderOption
      }
      sx={sx}
      componentsProps={optionsWidth != null ? componentsProps : undefined}
    />
  );
};

function defaultRenderOption(
  valueAttribute = 'value',
  labelAttribute = 'label',
  renderedOptionsRef?: React.MutableRefObject<Option[] | null>,
) {
  return (props: HTMLAttributes<HTMLLIElement>, option: Option) => {
    const val = option[labelAttribute] ?? option[valueAttribute];
    const strVal = typeof val === 'string' ? val : String(val);
    if (
      renderedOptionsRef &&
      renderedOptionsRef.current &&
      !renderedOptionsRef.current.some(
        (storedOption) =>
          storedOption[valueAttribute] === option[valueAttribute],
      )
    ) {
      renderedOptionsRef.current.push(option);
    }
    return (
      <li {...props}>
        <Typography noWrap aria-label={strVal}>
          {strVal}
        </Typography>
      </li>
    );
  };
}

export const VirtualizedSelectInput = React.forwardRef(
  <T extends Option>(
    {
      id,
      autoCompleteClasses = {},
      open,
      autoFocus,
      openOnFocus,
      onBlur,
      onFocus,
      onOpen,
      onMouseDown,
      onKeyDown,
      label,
      value,
      inputValue,
      onChange,
      onInputChange,
      options,
      loading,
      disableClearable = false,
      placeholder,
      helpText,
      className,
      groupBy,
      disableUnderline = false,
      startIcon,
      endIcon,
      renderOption,
      disabled,
      size,
      variant,
      valueAttribute = 'value',
      labelAttribute = 'label',
      required,
      fullWidth,
      'data-testid': dataTestId,
      IOInputProps,
      labelColor,
      labelFontWeight,
      labelFontStyle,
      sx,
      multi,
      limitTags,
      formHorizontal,
      error,
      unknownValueMessage,
      delimiter,
      selectOptionOnTab,
      AsyncSelect,
      selectOptionOnBlur,
      disableSelection,
      isGridHeaderFilter = false,
      hideSelectedOptions = false,
      optionsWidth,
      ignoreSpecialCharacters,
      specialCharacters,
    }: SelectInputProps<T>,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const renderedOptionsRef = useRef<Option[] | null>([]);
    const optionRef = useRef<T | null>(null);
    const selectedOptionsRef = useRef<T[]>([]);
    const selectedOption = multi
      ? options.filter((option) => {
          const values =
            typeof value === 'string' ? value?.split(delimiter ?? ';') : [];
          return values.includes(
            typeof option[valueAttribute] === 'string'
              ? (option[valueAttribute] as string)
              : String(option[valueAttribute]),
          );
        })
      : options.find((option) => {
          if (typeof value === 'number') {
            return Number(option[valueAttribute]) === value;
          } else {
            return option[valueAttribute] === value;
          }
        });

    const filteredOptions = useMemo(() => {
      if (
        multi &&
        hideSelectedOptions &&
        Array.isArray(selectedOption) &&
        selectedOption.length
      ) {
        const selectedValues = new Set(
          selectedOption.map((sel) => sel[valueAttribute]),
        );
        return options.filter(
          (opt) => !selectedValues.has(opt[valueAttribute]),
        );
      }
      return options;
    }, [multi, hideSelectedOptions, selectedOption, options, valueAttribute]);

    const [isTabPressed, setIsTabPressed] = useState(false);
    let unknownValue: React.ReactNode;
    let unknownMultiValue = '';
    let errorHelpText = '';
    if (multi && value) {
      const values =
        typeof value === 'string' ? value?.split(delimiter ?? ';') : [];
      values.forEach((v) => {
        const find = options.find((o) => {
          return (
            v ===
              (typeof o[labelAttribute] === 'string'
                ? o[labelAttribute]
                : String(o[labelAttribute])) ||
            v ===
              (typeof o[valueAttribute] === 'string'
                ? o[valueAttribute]
                : String(o[valueAttribute]))
          );
        });
        if (!find) {
          unknownMultiValue = isEmpty(unknownMultiValue)
            ? `${v}`
            : `${unknownMultiValue}, ${v}`;
        }
      });
    }
    if (
      value &&
      (!selectedOption ||
        (multi && selectedOption.length && unknownMultiValue)) &&
      options.length
    ) {
      if (typeof value !== 'string') {
        value = JSON.stringify(value);
      }
      unknownValue = (
        <>
          {`${
            unknownValueMessage
              ? unknownValueMessage
              : multi
              ? `Unknown value(s) [${unknownMultiValue}]!`
              : `Unknown value [${value}]!`
          }`}
          {disabled ? null : (
            <Box
              component="span"
              sx={{ cursor: 'pointer', color: 'primary.dark', marginLeft: 1 }}
              onClick={() => {
                onChange('', null);
              }}
            >
              Clear
            </Box>
          )}
        </>
      );
      errorHelpText = `${
        unknownValueMessage
          ? unknownValueMessage
          : multi
          ? `Unknown value(s) [${unknownMultiValue}]!`
          : `Unknown value [${value}]!`
      }`;
    }
    const _onChange = (option: T | T[] | null) => {
      if (option) {
        if (Array.isArray(option)) {
          const optionsValue = option
            .map((v) => v[valueAttribute])
            .join(delimiter ?? ';');
          onChange(optionsValue, null, option);
        } else {
          onChange(option[valueAttribute] as string, option);
        }
      } else {
        onChange('', option);
      }
    };
    const classes = useStyles({ size });
    // eslint-disable-next-line testing-library/render-result-naming-convention
    let renderOptionFn = renderOption;
    if (!renderOptionFn) {
      renderOptionFn = defaultRenderOption(
        valueAttribute,
        labelAttribute,
        renderedOptionsRef,
      );
    }
    const handleSelection = () => {
      if (multi && optionRef.current) {
        const _option = optionRef.current;
        const isOptionSelected = selectedOptionsRef.current.includes(_option);
        if (!isOptionSelected) {
          selectedOptionsRef.current = [...selectedOptionsRef.current, _option];
        } else {
          selectedOptionsRef.current = selectedOptionsRef.current.filter(
            (option) => option !== _option,
          );
        }
        _onChange(selectedOptionsRef.current);
      } else {
        _onChange(optionRef.current);
      }
    };
    return (
      <Autocomplete
        id={id}
        fullWidth={fullWidth}
        open={open}
        PopperComponent={IOPopper}
        openOnFocus={openOnFocus}
        onFocus={onFocus}
        onOpen={onOpen}
        onMouseDown={onMouseDown}
        onHighlightChange={(event, option, reason) => {
          if (option) {
            optionRef.current = option;
          } else if (!multi) {
            if (renderedOptionsRef.current && !AsyncSelect) {
              optionRef.current = renderedOptionsRef.current[0] as T;
            } else if (AsyncSelect && options) {
              const optionVal = options.find((opt) => {
                const labelValue = opt[labelAttribute];
                const labelString =
                  labelValue !== null && labelValue !== undefined
                    ? String(labelValue).toLowerCase()
                    : '';
                return labelString.includes(
                  inputValue?.toString().toLowerCase() || '',
                );
              });
              if (optionVal) {
                optionRef.current = optionVal;
              }
            }
          }
        }}
        onBlur={(e) => {
          if (selectOptionOnBlur && !disableSelection) {
            if (optionRef.current && !isTabPressed) {
              handleSelection();
            }
            if (isTabPressed) {
              setIsTabPressed(false);
            }
          }
          onBlur && onBlur(e);
        }}
        onKeyDown={(e) => {
          if (isGridHeaderFilter) {
            e.stopPropagation();
          }
          if (e.key === 'Tab') {
            if (selectOptionOnTab && optionRef.current && !disableSelection) {
              handleSelection();
            }
            setIsTabPressed(true);
          }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.stopPropagation();
          } else if (onKeyDown) {
            onKeyDown(e);
          }
        }}
        multiple={multi}
        disabled={disabled}
        value={selectedOption ?? null}
        inputValue={
          typeof inputValue === 'number' ? String(inputValue) : inputValue
        }
        disableClearable={disableClearable}
        options={filteredOptions}
        loading={loading}
        getOptionLabel={(option: T) => {
          const labelVal = option[labelAttribute] ?? option[valueAttribute];
          return typeof labelVal === 'string' ? labelVal : String(labelVal);
        }}
        groupBy={groupBy}
        className={className}
        onChange={(event, option: T | T[] | null | string) => {
          optionRef.current = null;
          if (multi && Array.isArray(option)) {
            selectedOptionsRef.current = [...option];
          }
          _onChange(option as T | T[] | null);
        }}
        onInputChange={(event, val: string, reason) => {
          if (!(event?.type === 'blur')) optionRef.current = null;
          if (onInputChange) {
            onInputChange(val, reason);
          }
        }}
        filterOptions={
          ignoreSpecialCharacters
            ? (filteredOpts, state) => {
                const charsToStrip = specialCharacters
                  ? specialCharacters.split(';').map((c) => c.trim())
                  : null;
                const stripSpecial = (str: string) => {
                  if (charsToStrip && charsToStrip.length > 0) {
                    return charsToStrip.reduce(
                      (s, c) => s.split(c).join(''),
                      str,
                    );
                  }
                  return str.replace(/[^a-zA-Z0-9\s]/g, '');
                };
                const input = stripSpecial(state.inputValue.toLowerCase());
                return filteredOpts.filter((option) => {
                  const labelVal =
                    option[labelAttribute] ?? option[valueAttribute];
                  const optLabel =
                    typeof labelVal === 'string' ? labelVal : String(labelVal);
                  return stripSpecial(optLabel.toLowerCase()).includes(input);
                });
              }
            : undefined
        }
        disableListWrap
        classes={{
          root: autoCompleteClasses.root,
          inputRoot: clsx(
            disableUnderline && !autoCompleteClasses.inputRoot
              ? classes.inputRoot
              : undefined,
            autoCompleteClasses.inputRoot,
          ),
          input: clsx(classes.input, autoCompleteClasses.input),
          listbox: clsx(classes.listbox, autoCompleteClasses.listbox),
          popper: clsx(classes.popper, autoCompleteClasses.popper),
          endAdornment: autoCompleteClasses.endAdornment,
        }}
        ListboxComponent={(props) => (
          <div onWheel={stopPropagation}>
            <ListboxComponent
              {...props}
              multi={multi}
              selectOptionOnTab={selectOptionOnTab}
            />
          </div>
        )}
        renderGroup={renderGroup}
        renderInput={(params) => {
          return (
            <CustomTextField
              data-testid={dataTestId}
              ref={ref}
              autoFocus={autoFocus}
              params={params}
              label={formHorizontal ? undefined : label}
              placeholder={placeholder}
              helpText={errorHelpText || helpText}
              disableUnderline={disableUnderline}
              startIcon={startIcon}
              endIcon={endIcon}
              variant={variant}
              required={required}
              IOInputProps={IOInputProps}
              error={error || !!unknownValue}
              labelColor={labelColor}
              labelFontWeight={labelFontWeight}
              labelFontStyle={labelFontStyle}
            />
          );
        }}
        renderOption={renderOptionFn}
        autoComplete
        autoHighlight
        selectOnFocus
        componentsProps={{
          popper:
            optionsWidth != null
              ? { style: { width: optionsWidth, maxWidth: 'none' } }
              : undefined,

          paper: {
            style:
              optionsWidth != null
                ? {
                    minWidth: optionsWidth,
                    width: optionsWidth,
                    overflowX: 'auto',
                  }
                : {
                    minWidth: 'fit-content',
                  },
          },
        }}
        sx={sx}
        size={size}
        limitTags={multi && limitTags && limitTags > 0 ? limitTags : undefined}
      />
    );
  },
);

export const VirtualizedMultiSelectInput = React.forwardRef(
  <T extends Option>(
    {
      id,
      autoCompleteClasses = {},
      open,
      autoFocus,
      openOnFocus,
      onBlur,
      onMouseDown,
      onKeyDown,
      label,
      value,
      inputValue,
      onChange,
      onInputChange,
      options,
      disableClearable = false,
      placeholder,
      helpText,
      className,
      groupBy,
      disableUnderline = false,
      startIcon,
      endIcon,
      renderOption,
      disabled,
      size,
      variant,
      valueAttribute = 'value',
      labelAttribute = 'label',
      required,
      fullWidth,
      'data-testid': dataTestId,
      IOInputProps,
      limitTags,
      sx,
    }: MultiSelectInputProps<T>,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const selectedOptions = value
      .map((val) => options.find((option) => option[valueAttribute] === val))
      .filter((v) => v !== undefined) as T[];
    const _onChange = (values: T[] = []) => {
      onChange(
        values.map((o) => o[valueAttribute] as string),
        values,
      );
    };
    const classes = useStyles({ size });
    // eslint-disable-next-line testing-library/render-result-naming-convention
    let renderOptionFn = renderOption;
    if (!renderOptionFn) {
      renderOptionFn = defaultRenderOption(valueAttribute, labelAttribute);
    }
    return (
      <Autocomplete
        id={id}
        fullWidth={fullWidth}
        open={open}
        PopperComponent={IOPopper}
        multiple
        limitTags={limitTags}
        size="small"
        openOnFocus={openOnFocus}
        onBlur={onBlur}
        onMouseDown={onMouseDown}
        onKeyDown={onKeyDown}
        disabled={disabled}
        value={selectedOptions}
        inputValue={inputValue}
        disableClearable={disableClearable}
        options={options}
        getOptionLabel={(option: T) => {
          const labelVal = option[labelAttribute] ?? option[valueAttribute];
          return typeof labelVal === 'string' ? labelVal : String(labelVal);
        }}
        groupBy={groupBy}
        className={className}
        onChange={(event, option: T[]) => {
          _onChange(option as T[]);
        }}
        onInputChange={(event, val: string, reason) => {
          if (onInputChange) {
            onInputChange(val, reason);
          }
        }}
        filterOptions={onInputChange ? (x) => x : undefined}
        disableListWrap
        classes={{
          root: autoCompleteClasses.root,
          inputRoot: clsx(
            disableUnderline && !autoCompleteClasses.inputRoot
              ? classes.inputRoot
              : undefined,
            autoCompleteClasses.inputRoot,
          ),
          input: clsx(classes.input, autoCompleteClasses.input),
          listbox: clsx(classes.listbox, autoCompleteClasses.listbox),
          popper: clsx(classes.popper, autoCompleteClasses.popper),
          endAdornment: autoCompleteClasses.endAdornment,
        }}
        ListboxComponent={ListboxComponent}
        renderGroup={renderGroup}
        renderInput={(params) => {
          return (
            <CustomTextField
              data-testid={dataTestId}
              ref={ref}
              autoFocus={autoFocus}
              params={params}
              label={label}
              placeholder={placeholder}
              helpText={helpText}
              disableUnderline={disableUnderline}
              startIcon={startIcon}
              endIcon={endIcon}
              variant={variant}
              required={required}
              IOInputProps={IOInputProps}
            />
          );
        }}
        renderOption={renderOptionFn}
        componentsProps={{
          paper: {
            style: { minWidth: 'fit-content' },
          },
        }}
        sx={sx}
      />
    );
  },
);

interface CustomTextFieldProps {
  autoFocus?: boolean;
  params: any;
  inputClasses?: any;
  labelClasses?: any;
  label?: string;
  placeholder?: string;
  helpText?: React.ReactNode;
  disableUnderline?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  variant: string;
  required?: boolean;
  IOInputProps?: Partial<InputProps>;
  error?: boolean;
  'data-testid'?: string;
  labelColor?: string;
  labelFontWeight?: number;
  labelFontStyle?: boolean;
}

export const CustomTextField = React.forwardRef(function CustomTextField(
  {
    autoFocus,
    params = { InputLabelProps: {}, InputProps: {} },
    label,
    placeholder,
    helpText,
    disableUnderline = false,
    startIcon,
    endIcon,
    variant,
    required,
    IOInputProps = {},
    error,
    'data-testid': dataTestId,
    labelColor,
    labelFontWeight,
    labelFontStyle,
  }: CustomTextFieldProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const startAdornment = (
    <>
      {startIcon}
      {IOInputProps.startAdornment}
      {params?.InputProps?.startAdornment}
    </>
  );

  const endAdornment = (
    <>
      {endIcon}
      {IOInputProps.endAdornment}
      {params?.InputProps?.endAdornment}
    </>
  );

  const jref = useJoinRef(ref, params?.InputProps?.ref, IOInputProps.inputRef);
  const FinalInputProps: Partial<InputProps> =
    variant !== 'outlined'
      ? {
          ...params.InputProps,
          ...IOInputProps,
          inputRef: jref,
          disableUnderline,
          startAdornment,
          endAdornment,
        }
      : {
          ...params.InputProps,
          ...IOInputProps,
          inputRef: jref,
          startAdornment,
          endAdornment,
        };
  const tip = helpText
    ? {
        'data-tip': helpText,
        'data-tip-at': 'top',
        'data-tip-error': true,
        'data-tip-delay': 0,
      }
    : undefined;
  return (
    <TextField
      {...params}
      {...tip}
      autoFocus={autoFocus}
      helperText={helpText}
      label={label}
      placeholder={placeholder}
      InputLabelProps={{
        ...params.InputLabelProps,
        sx: {
          ...params.InputLabelProps.sx,
          color: labelColor,
          fontWeight: labelFontWeight,
          fontStyle: labelFontStyle ? 'italic' : undefined,
          '&.Mui-disabled': {
            color: labelColor,
          },
        },
      }}
      InputProps={FinalInputProps}
      variant={variant}
      required={required}
      error={error}
      inputProps={{
        ...params.inputProps,
        ...IOInputProps,
        'data-testid': dataTestId,
      }}
    />
  );
});

export default React.forwardRef(SelectInput);

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef(
  (props, ref: React.Ref<HTMLDivElement>) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  },
);
OuterElementType.displayName = 'OuterElementType';

interface ListboxProps
  extends React.PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  multi?: boolean;
  selectOptionOnTab?: boolean;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(
  (props: ListboxProps, ref: React.Ref<HTMLDivElement>) => {
    const { children, multi, selectOptionOnTab, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const getHeight = () => {
      if (itemCount > 10) {
        return 10 * 36;
      }
      return itemCount * 36;
    };
    const selectedRowIndex = itemData.findIndex((a: any) => {
      return a?.props?.['aria-selected'] === true;
    });

    const height = getHeight();
    const s = useComponentSize();
    const [listWidth, setListWidth] = useState(s.width);

    return (
      <div ref={s.ref} style={{ ...other.style, minHeight: height + 16 }}>
        <VirtualList
          CellComponent={React.forwardRef(
            (
              {
                rowIndex,
                onMouseEnter,
                onMouseLeave,
                style,
                onFocusCapture,
                onFocus,
                onKeyDown,
                'aria-rowindex': ariaRowIndex,
                className,
                ...others
              },
              _ref,
            ) => {
              const swidth = s.width;
              const ref2 = useRef<HTMLDivElement>(null);
              const joinRef = useJoinRef(_ref, ref2);
              useEffect(() => {
                if (ref2.current && swidth) {
                  const maxWidth =
                    window.innerWidth -
                    ref2.current.getBoundingClientRect().left -
                    32 -
                    15; // 32 is the padding of the listbox, 15 is the scrollbar width
                  const width = Math.min(
                    maxWidth,
                    ref2.current.firstElementChild?.scrollWidth ?? 0,
                  );
                  if (width > listWidth) {
                    setListWidth((w) => {
                      if (w < width) {
                        return width;
                      }
                      return w;
                    });
                  }
                }
              }, [swidth]);
              return React.cloneElement(
                itemData[rowIndex] as React.ReactElement,
                {
                  onMouseEnter,
                  onMouseLeave,
                  onFocusCapture,
                  onFocus,
                  onKeyDown,
                  'aria-rowindex': ariaRowIndex,
                  style,
                  className: clsx(
                    className,
                    (itemData[rowIndex] as any).props?.className,
                  ),
                  ref: joinRef,
                },
              );
            },
          )}
          rowHeight={36}
          height={height}
          totalRows={itemData.length}
          width={Math.max(s.width, listWidth + 32 + 15)} // 15 is scrollbar width, 32 is padding
          scrollToRow={
            multi && selectOptionOnTab ? undefined : selectedRowIndex
          }
          custom
          showRowLines={false}
          bodyRef={ref}
          bodyProps={other}
          selectField={true}
        />
      </div>
    );
  },
);

export const useStyles = makeStyles((theme: Theme) => ({
  inputRoot: {
    '& $input': {
      paddingLeft: 8,
    },
    '&[class*="MuiInput-root"]': {
      '& $input': {
        paddingLeft: 8,
      },
      '& $input:first-of-type': {
        paddingLeft: 8,
      },
    },
  },
  input: ({ size }: { size?: SizeType }) => ({
    fontSize: size === 'small' ? '0.8rem' : '1rem',
  }),
  listbox: ({ size }: { size?: SizeType }) => ({
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    padding: 0,
    fontSize: size === 'small' ? '0.8rem' : '1rem',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  }),
  popper: {
    zIndex: zIndex.AutoCompletePopper,
  },
  endAdornment: {
    position: 'absolute',
    right: 0,
  },
}));

const renderGroup = (params: any) => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];
